"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import svgPaths from "@/assets/svg/svg-hslrms5ay1";
import svgPathsLanding from "@/assets/svg/svg-5s97axwnwx";
import { img } from "@/assets/svg/svg-chr98";
import imgHeroImageRight from "@/assets/images/landing/b7cf068f2cd000acb93c2d6dcf2c5d20fcc2d70c.png";
import ScalableContainer from "@/components/ui/ScalableContainer";
import Footer from "@/components/layout/Footer";
import SearchBar from "@/components/dashboard/SearchBar";
import type { AddressData } from "@/types/address";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import SampleSearchResultsPreview from "@/components/sections/landing/SampleSearchResultsPreview";

function AdsClick() {
	return (
		<div
			className="absolute left-1/2 size-[2.5rem] top-1/2 translate-x-[-50%] translate-y-[-50%]"
			data-name="ads_click"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 40 40"
			>
				<g id="ads_click">
					<mask
						height="40"
						id="mask0_9_12817"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="40"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #CBF7A6)"
							height="40"
							id="Bounding box"
							width="40"
						/>
					</mask>
					<g mask="url(#mask0_9_12817)">
						<g filter="url(#filter0_n_9_12817)" id="ads_click_2">
							<path
								d={svgPaths.p30826b00}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="38.3851"
						id="filter0_n_9_12817"
						width="38.3848"
						x="0.791016"
						y="0.791829"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2.25 2.25"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12817">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function FeatureIconContainer() {
	return (
		<div
			className="backdrop-blur-md backdrop-filter overflow-clip relative rounded-[5.3125rem] shrink-0 size-[4.5rem]"
			data-name="Feature Icon Container"
			style={{
				backgroundImage:
					"linear-gradient(90deg, rgba(101, 222, 158, 0.12) 0%, rgba(101, 222, 158, 0.12) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)",
			}}
		>
			<AdsClick />
		</div>
	);
}

function FeatureContainer() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] h-[22.75rem] items-start overflow-clip p-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[36rem] max-w-[24rem] group"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), rgba(22, 37, 28, 0.5)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer />
			<p
				className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[2.5rem] min-w-full relative shrink-0 text-[2rem] text-white tracking-[0.03125rem] w-[min-content]"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Our Mission
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full not-italic relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] tracking-[0.03125rem] w-[min-content]">
				We exist to give serious investors a faster, smarter, and more
				transparent way to research tax liens and deeds. With verified
				legal, financial, and property intelligence at your fingertips,
				Tax Lien Vest empowers you to make confident investment
				decisions in minutes, not days.
			</p>
			{/* Shining border effect */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.3), inset 0 0 20px rgba(167, 255, 94, 0.1)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function FeatureSection() {
	return <FeatureContainer />;
}

function Lightbulb() {
	return (
		<div
			className="absolute left-[1rem] size-[2.5rem] top-[1rem]"
			data-name="lightbulb"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 40 40"
			>
				<g id="lightbulb">
					<mask
						height="40"
						id="mask0_9_12856"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="40"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #D9D9D9)"
							height="40"
							id="Bounding box"
							width="40"
						/>
					</mask>
					<g mask="url(#mask0_9_12856)">
						<g filter="url(#filter0_n_9_12856)" id="lightbulb_2">
							<path
								d={svgPaths.p1da9e000}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="38.4167"
						id="filter0_n_9_12856"
						width="27.8333"
						x="6.08333"
						y="0.791666"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2.25 2.25"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12856">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function FeatureIconContainer1() {
	return (
		<div
			className="backdrop-blur-md backdrop-filter overflow-clip relative rounded-[5.3125rem] shrink-0 size-[4.5rem]"
			data-name="Feature Icon Container"
			style={{
				backgroundImage:
					"linear-gradient(90deg, rgba(101, 222, 158, 0.12) 0%, rgba(101, 222, 158, 0.12) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)",
			}}
		>
			<Lightbulb />
		</div>
	);
}

function FeatureContainer1() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] h-[22.75rem] items-start overflow-clip p-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[36rem] max-w-[24rem] group"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), rgba(22, 37, 28, 0.5)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer />
			<p
				className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[2.5rem] min-w-full relative shrink-0 text-[2rem] text-white tracking-[0.03125rem] w-[min-content]"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Our Vision
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full not-italic relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] tracking-[0.03125rem] w-[min-content]">
				We envision a market where tax lien investing is accessible,
				data-driven, and profitable for every investor—from seasoned
				professionals to those just starting their journey.
			</p>
			{/* Shining border effect */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.3), inset 0 0 20px rgba(167, 255, 94, 0.1)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function FeatureSection1() {
	return <FeatureContainer1 />;
}

function AboutUsHeroTitle() {
	return (
		<motion.div
			className="relative content-stretch flex flex-col gap-[1.25rem] items-start tracking-[0.03125rem] flex-1 min-w-[36.5rem]"
			initial={{ opacity: 0, y: 30 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
		>
			<motion.p
				className="font-['Bricolage_Grotesque:ExtraBold',sans-serif] font-extrabold leading-[3.5rem] min-w-full relative shrink-0 text-[3rem] text-white w-[min-content]"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
				initial={{ opacity: 0, x: -20 }}
				animate={{ opacity: 1, x: 0 }}
				transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
			>
				About Us
			</motion.p>
			<motion.p
				className="font-['Inter:Regular',sans-serif] font-normal leading-[2rem] not-italic relative shrink-0 text-[1.25rem] text-[rgba(255,255,255,0.7)] w-[36.5rem]"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
			>
				Welcome to Tax Lien Vest, the professional-grade platform that
				transforms complex tax lien property research into confident,
				profitable investments.
			</motion.p>
		</motion.div>
	);
}

function AboutUsHeroSection() {
	return (
		<div
			className="relative bg-[#16251c] min-h-[29.125rem] overflow-clip w-full flex flex-col items-center justify-center"
			data-name="Hero Section"
		>
			<div className="absolute left-[-8.625rem] size-[29.125rem] top-[-11.5625rem] hero-shadow-bg">
				<div className="absolute inset-[-85.837%]">
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 1266 1266"
					>
						<g filter="url(#filter0_fn_0_10344)" id="Ellipse 852">
							<circle
								cx="633"
								cy="633"
								fill="var(--fill-0, #044B27)"
								r="233"
							/>
						</g>
						<defs>
							<filter
								colorInterpolationFilters="sRGB"
								filterUnits="userSpaceOnUse"
								height="1266"
								id="filter0_fn_0_10344"
								width="1266"
								x="0"
								y="0"
							>
								<feFlood
									floodOpacity="0"
									result="BackgroundImageFix"
								/>
								<feBlend
									in="SourceGraphic"
									in2="BackgroundImageFix"
									mode="normal"
									result="shape"
								/>
								<feGaussianBlur
									result="effect1_foregroundBlur_0_10344"
									stdDeviation="200"
								/>
								<feTurbulence
									baseFrequency="0.40000000596046448 0.40000000596046448"
									numOctaves="3"
									result="noise"
									seed="234"
									stitchTiles="stitch"
									type="fractalNoise"
								/>
								<feColorMatrix
									in="noise"
									result="alphaNoise"
									type="luminanceToAlpha"
								/>
								<feComponentTransfer
									in="alphaNoise"
									result="coloredNoise1"
								>
									<feFuncA
										tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
										type="discrete"
									/>
								</feComponentTransfer>
								<feComposite
									in="coloredNoise1"
									in2="effect1_foregroundBlur_0_10344"
									operator="in"
									result="noise1Clipped"
								/>
								<feFlood
									floodColor="rgba(255, 255, 255, 0.12)"
									result="color1Flood"
								/>
								<feComposite
									in="color1Flood"
									in2="noise1Clipped"
									operator="in"
									result="color1"
								/>
								<feMerge result="effect2_noise_0_10344">
									<feMergeNode in="effect1_foregroundBlur_0_10344" />
									<feMergeNode in="color1" />
								</feMerge>
							</filter>
						</defs>
					</svg>
				</div>
			</div>
			<div className="relative flex flex-row flex-wrap gap-8 items-center w-full px-[7.5rem]">
				<AboutUsHeroTitle />
			</div>
			<div
				className="absolute h-[26.75rem] left-[48.1875rem] top-[9.625rem] w-[42.97075rem]"
				data-name="Hero Image Right"
			>
				<Image
					alt=""
					className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none size-full"
					src={imgHeroImageRight}
					fill
					sizes="(max-width: 1440px) 50vw, 687px"
				/>
			</div>
		</div>
	);
}

function ExperimentIcon() {
	return (
		<div
			className="h-[4.420562rem] relative shrink-0 w-[4.5rem]"
			data-name="experiment"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 72 71"
			>
				<g filter="url(#filter0_n_9_12815)" id="experiment">
					<path
						d={svgPaths.p23de3d00}
						fill="var(--fill-0, #25BC45)"
					/>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="70.7291"
						id="filter0_n_9_12815"
						width="72"
						x="0"
						y="0"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2 2"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12815">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function FeatureDescriptionContainer() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.25rem] items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
		>
			<p className="font-['Inter:Black',sans-serif] font-black leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Smarter Property Research
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				Access geographic, financial, legal, and owner data to screen,
				analyse, and act on opportunities with confidence.
			</p>
		</div>
	);
}

function FeatureIconContainer2() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem]"
			data-name="Feature Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 72 72"
			>
				<g id="Feature Icon Container">
					<g filter="url(#filter1_n_0_10354)" id="villa">
						<path
							d={svgPathsLanding.p2ddd5a00}
							fill="var(--fill-0, #25BC45)"
						/>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="64"
						id="filter1_n_0_10354"
						width="64"
						x="0"
						y="-4.92599e-09"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2 2"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_0_10354">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function FeatureDescriptionContainerVerified() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.25rem] items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
		>
			<p className="font-['Inter:Black',sans-serif] font-black leading-[2rem] relative shrink-0 text-[1.25rem] text-white break-words">
				Verified Property Insights
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] break-words">
				Get complete, up-to-date property profiles with every crucial
				detail investors need.
			</p>
		</div>
	);
}

function FeatureContainer2() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] h-[20.25rem] items-start overflow-clip p-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[24rem] max-w-[24rem] group"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), rgba(22, 37, 28, 0.5)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer2 />
			<FeatureDescriptionContainerVerified />
			{/* Shining border effect */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.3), inset 0 0 20px rgba(167, 255, 94, 0.1)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function GavelIcon() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem]"
			data-name="Feature Icon Container"
		>
			<div className="absolute bottom-[-2.65%] left-0 right-0 top-[-2.78%]">
				<svg
					className="block size-full"
					fill="none"
					preserveAspectRatio="none"
					viewBox="0 0 72 76"
				>
					<g id="Feature Icon Container">
						<g filter="url(#filter1_n_9_12843)" id="gavel">
							<path
								d={svgPaths.p38980300}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
					<defs>
						<filter
							colorInterpolationFilters="sRGB"
							filterUnits="userSpaceOnUse"
							height="75.907"
							id="filter1_n_9_12843"
							width="72"
							x="0.00019455"
							y="0"
						>
							<feFlood
								floodOpacity="0"
								result="BackgroundImageFix"
							/>
							<feBlend
								in="SourceGraphic"
								in2="BackgroundImageFix"
								mode="normal"
								result="shape"
							/>
							<feTurbulence
								baseFrequency="2 2"
								numOctaves="3"
								result="noise"
								seed="662"
								stitchTiles="stitch"
								type="fractalNoise"
							/>
							<feColorMatrix
								in="noise"
								result="alphaNoise"
								type="luminanceToAlpha"
							/>
							<feComponentTransfer
								in="alphaNoise"
								result="coloredNoise1"
							>
								<feFuncA
									tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
									type="discrete"
								/>
							</feComponentTransfer>
							<feComposite
								in="coloredNoise1"
								in2="shape"
								operator="in"
								result="noise1Clipped"
							/>
							<feFlood
								floodColor="rgba(0, 0, 0, 0.37)"
								result="color1Flood"
							/>
							<feComposite
								in="color1Flood"
								in2="noise1Clipped"
								operator="in"
								result="color1"
							/>
							<feMerge result="effect1_noise_9_12843">
								<feMergeNode in="shape" />
								<feMergeNode in="color1" />
							</feMerge>
						</filter>
					</defs>
				</svg>
			</div>
		</div>
	);
}

function FeatureDescriptionContainer1() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.75rem] items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
		>
			<p className="font-['Inter:Bold',sans-serif] font-bold leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Auction-Ready Intelligence
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				From sheriff sale dates to total debt amounts, we provide the
				data you need to prepare winning bids.
			</p>
		</div>
	);
}

function FeatureContainer3() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] h-[20.25rem] items-start overflow-clip p-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[24rem] max-w-[24rem] group"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), rgba(22, 37, 28, 0.5)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<GavelIcon />
			<FeatureDescriptionContainer1 />
			{/* Shining border effect */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.3), inset 0 0 20px rgba(167, 255, 94, 0.1)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function MarkerPinIcon() {
	return (
		<div
			className="overflow-clip relative shrink-0 size-[4.5rem]"
			data-name="marker-pin-04"
		>
			<div className="absolute inset-[8.333%]" data-name="Icon">
				<div
					className="absolute inset-[-6.667%]"
					style={
						{
							"--stroke-0": "rgba(35, 175, 65, 1)",
						} as React.CSSProperties
					}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 68 68"
					>
						<g filter="url(#filter0_n_9_12846)" id="Icon">
							<path
								d={svgPaths.pfefe500}
								stroke="var(--stroke-0, #23AF41)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="8"
							/>
						</g>
						<defs>
							<filter
								colorInterpolationFilters="sRGB"
								filterUnits="userSpaceOnUse"
								height="68"
								id="filter0_n_9_12846"
								width="68"
								x="0"
								y="0"
							>
								<feFlood
									floodOpacity="0"
									result="BackgroundImageFix"
								/>
								<feBlend
									in="SourceGraphic"
									in2="BackgroundImageFix"
									mode="normal"
									result="shape"
								/>
								<feTurbulence
									baseFrequency="2 2"
									numOctaves="3"
									result="noise"
									seed="662"
									stitchTiles="stitch"
									type="fractalNoise"
								/>
								<feColorMatrix
									in="noise"
									result="alphaNoise"
									type="luminanceToAlpha"
								/>
								<feComponentTransfer
									in="alphaNoise"
									result="coloredNoise1"
								>
									<feFuncA
										tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
										type="discrete"
									/>
								</feComponentTransfer>
								<feComposite
									in="coloredNoise1"
									in2="shape"
									operator="in"
									result="noise1Clipped"
								/>
								<feFlood
									floodColor="rgba(0, 0, 0, 0.37)"
									result="color1Flood"
								/>
								<feComposite
									in="color1Flood"
									in2="noise1Clipped"
									operator="in"
									result="color1"
								/>
								<feMerge result="effect1_noise_9_12846">
									<feMergeNode in="shape" />
									<feMergeNode in="color1" />
								</feMerge>
							</filter>
						</defs>
					</svg>
				</div>
			</div>
		</div>
	);
}

function FeatureDescriptionContainer2() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.75rem] items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
		>
			<p className="font-['Inter:Bold',sans-serif] font-bold leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Opportunity Zone Targeting
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				Find tax-advantaged properties using our integrated Qualified
				Opportunity Zone filters.
			</p>
		</div>
	);
}

function FeatureContainer4() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] h-[20.25rem] items-start overflow-clip p-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[24rem] max-w-[24rem] group"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.06) 0%, transparent 45%), rgba(22, 37, 28, 0.5)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<MarkerPinIcon />
			<FeatureDescriptionContainer2 />
			{/* Shining border effect */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.3), inset 0 0 20px rgba(167, 255, 94, 0.1)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function Beenhere() {
	return (
		<div className="relative shrink-0 size-[2.5rem]" data-name="beenhere">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 40 40"
			>
				<g id="beenhere">
					<mask
						height="40"
						id="mask0_9_12835"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="40"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #D9D9D9)"
							height="40"
							id="Bounding box"
							width="40"
						/>
					</mask>
					<g mask="url(#mask0_9_12835)">
						<g filter="url(#filter0_n_9_12835)" id="beenhere_2">
							<path
								d={svgPaths.p3e271900}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="36.7917"
						id="filter0_n_9_12835"
						width="29.5"
						x="5.25065"
						y="1.49999"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="3.5999999046325684 3.5999999046325684"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12835">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function TrustPoint1() {
	return (
		<div className="basis-0 content-stretch flex flex-col gap-[0.5rem] grow items-start min-h-px min-w-px relative shrink-0">
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-[2rem] not-italic relative shrink-0 text-[1.25rem] text-nowrap text-white tracking-[0.03125rem] whitespace-pre">
				Real-time data verified against court and parcel records.
			</p>
		</div>
	);
}

function FeatureDescriptionContainer3() {
	return (
		<motion.div
			className="content-stretch flex gap-[1.5rem] items-center relative shrink-0 flex-1"
			data-name="Feature Description Container"
			initial={{ opacity: 0, x: -30 }}
			animate={{ opacity: 1, x: 0 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.5, ease: "easeOut" }}
		>
			<Beenhere />
			<TrustPoint1 />
		</motion.div>
	);
}

function Beenhere1() {
	return (
		<div className="relative shrink-0 size-[2.5rem]" data-name="beenhere">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 40 40"
			>
				<g id="beenhere">
					<mask
						height="40"
						id="mask0_9_12835"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="40"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #D9D9D9)"
							height="40"
							id="Bounding box"
							width="40"
						/>
					</mask>
					<g mask="url(#mask0_9_12835)">
						<g filter="url(#filter0_n_9_12835)" id="beenhere_2">
							<path
								d={svgPaths.p3e271900}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="36.7917"
						id="filter0_n_9_12835"
						width="29.5"
						x="5.25065"
						y="1.49999"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="3.5999999046325684 3.5999999046325684"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12835">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function TrustPoint2() {
	return (
		<div className="basis-0 content-stretch flex flex-col gap-[0.5rem] grow items-start min-h-px min-w-px relative shrink-0">
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-[2rem] not-italic relative shrink-0 text-[1.25rem] text-nowrap text-white tracking-[0.03125rem] whitespace-pre">
				Comprehensive coverage across multiple states.
			</p>
		</div>
	);
}

function FeatureDescriptionContainer4() {
	return (
		<motion.div
			className="content-stretch flex gap-[1.5rem] items-center relative shrink-0 flex-1"
			data-name="Feature Description Container"
			initial={{ opacity: 0, x: -30 }}
			animate={{ opacity: 1, x: 0 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
		>
			<Beenhere1 />
			<TrustPoint2 />
		</motion.div>
	);
}

function Beenhere2() {
	return (
		<div className="relative shrink-0 size-[2.5rem]" data-name="beenhere">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 40 40"
			>
				<g id="beenhere">
					<mask
						height="40"
						id="mask0_9_12835"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="40"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #D9D9D9)"
							height="40"
							id="Bounding box"
							width="40"
						/>
					</mask>
					<g mask="url(#mask0_9_12835)">
						<g filter="url(#filter0_n_9_12835)" id="beenhere_2">
							<path
								d={svgPaths.p3e271900}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="36.7917"
						id="filter0_n_9_12835"
						width="29.5"
						x="5.25065"
						y="1.49999"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="3.5999999046325684 3.5999999046325684"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12835">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function TrustPoint3() {
	return (
		<div className="basis-0 content-stretch flex flex-col gap-[0.5rem] grow items-start min-h-px min-w-px relative shrink-0">
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-[2rem] not-italic relative shrink-0 text-[1.25rem] text-nowrap text-white tracking-[0.03125rem] whitespace-pre">
				Trusted by professionals to cut research time by up to 50% and
				maximize auction opportunities.
			</p>
		</div>
	);
}

function FeatureDescriptionContainer5() {
	return (
		<motion.div
			className="content-stretch flex gap-[1.5rem] items-center relative shrink-0 flex-1"
			data-name="Feature Description Container"
			initial={{ opacity: 0, x: -30 }}
			animate={{ opacity: 1, x: 0 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
		>
			<Beenhere2 />
			<TrustPoint3 />
		</motion.div>
	);
}

function WhyInvestorsTrustUs() {
	return (
		<div
			className="relative content-stretch flex flex-col gap-[2.25rem] items-start w-full"
			style={{ paddingTop: "3.5rem" }}
		>
			<p
				className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[2.75rem] relative shrink-0 text-[2rem] text-white tracking-[0.03125rem] w-full"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Why Investors Trust Us
			</p>
			<div className="relative flex flex-col flex-wrap gap-6 justify-between w-full">
				<FeatureDescriptionContainer3 />
				<FeatureDescriptionContainer4 />
				<FeatureDescriptionContainer5 />
			</div>
		</div>
	);
}

function Interests() {
	return (
		<div className="relative shrink-0 size-[4rem]" data-name="interests">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 64 64"
			>
				<g id="interests">
					<mask
						height="64"
						id="mask0_9_12850"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="64"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #D9D9D9)"
							height="64"
							id="Bounding box"
							width="64"
						/>
					</mask>
					<g mask="url(#mask0_9_12850)">
						<g filter="url(#filter0_n_9_12850)" id="interests_2">
							<path
								d={svgPaths.p6c87172}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="58.7333"
						id="filter0_n_9_12850"
						width="60.6067"
						x="3.12812"
						y="1.5334"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2.25 2.25"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12850">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function TransparencyContent() {
	return (
		<div className="basis-0 content-stretch flex flex-col gap-[0.5rem] grow items-start min-h-px min-w-px not-italic relative shrink-0 tracking-[0.03125rem]">
			<p className="font-['Inter:Black',sans-serif] font-black leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Transparency
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				All our data is sourced from official legal and financial
				records.
			</p>
		</div>
	);
}

function FeatureDescriptionContainer6() {
	return (
		<motion.div
			style={{ maxHeight: "5rem" }}
			className="content-stretch flex gap-[1.5rem] items-start relative shrink-0 flex-1"
			data-name="Feature Description Container"
			initial={{ opacity: 0, x: -30 }}
			animate={{ opacity: 1, x: 0 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.5, ease: "easeOut" }}
		>
			<Interests />
			<TransparencyContent />
		</motion.div>
	);
}

function LocalPolice() {
	return (
		<div className="relative shrink-0 size-[4rem]" data-name="local_police">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 64 64"
			>
				<g id="local_police">
					<mask
						height="64"
						id="mask0_9_12823"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="64"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #D9D9D9)"
							height="64"
							id="Bounding box"
							width="64"
						/>
					</mask>
					<g mask="url(#mask0_9_12823)">
						<g filter="url(#filter0_n_9_12823)" id="local_police_2">
							<path
								d={svgPaths.p2bc86f00}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="57"
						id="filter0_n_9_12823"
						width="47.2"
						x="8.40104"
						y="3.53337"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2.25 2.25"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12823">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function IntegrityContent() {
	return (
		<div className="basis-0 content-stretch flex flex-col gap-[0.5rem] grow items-start min-h-px min-w-px not-italic relative shrink-0 tracking-[0.03125rem]">
			<p className="font-['Inter:Black',sans-serif] font-black leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Integrity
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				We put investors&apos; interests first in every decision.
			</p>
		</div>
	);
}

function FeatureDescriptionContainer7() {
	return (
		<motion.div
			style={{ maxHeight: "5rem" }}
			className="content-stretch flex gap-[1.5rem] items-start relative shrink-0 flex-1"
			data-name="Feature Description Container"
			initial={{ opacity: 0, x: -30 }}
			animate={{ opacity: 1, x: 0 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
		>
			<LocalPolice />
			<IntegrityContent />
		</motion.div>
	);
}

function RocketLaunch() {
	return (
		<div
			className="relative shrink-0 size-[4rem]"
			data-name="rocket_launch"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 64 64"
			>
				<g id="rocket_launch">
					<mask
						height="64"
						id="mask0_9_12795"
						maskUnits="userSpaceOnUse"
						style={{ maskType: "alpha" }}
						width="64"
						x="0"
						y="0"
					>
						<rect
							fill="var(--fill-0, #D9D9D9)"
							height="64"
							id="Bounding box"
							width="64"
						/>
					</mask>
					<g mask="url(#mask0_9_12795)">
						<g
							filter="url(#filter0_n_9_12795)"
							id="rocket_launch_2"
						>
							<path
								d={svgPaths.p221266f0}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="60.2794"
						id="filter0_n_9_12795"
						width="60.0128"
						x="2.38723"
						y="2.46663"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2.25 2.25"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_9_12795">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function InnovationContent() {
	return (
		<div className="basis-0 content-stretch flex flex-col gap-[0.5rem] grow items-start min-h-px min-w-px not-italic relative shrink-0 tracking-[0.03125rem]">
			<p className="font-['Inter:Black',sans-serif] font-black leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Innovation
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				We continuously evolve our tools so you can stay ahead in a
				competitive market.
			</p>
		</div>
	);
}

function FeatureDescriptionContainer8() {
	return (
		<motion.div
			style={{ maxHeight: "5rem" }}
			className="content-stretch flex gap-[1.5rem] items-start relative shrink-0 flex-1"
			data-name="Feature Description Container"
			initial={{ opacity: 0, x: -30 }}
			animate={{ opacity: 1, x: 0 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
		>
			<RocketLaunch />
			<InnovationContent />
		</motion.div>
	);
}

function OurValues() {
	return (
		<div
			className="relative content-stretch flex flex-col gap-[2.25rem] items-start w-full"
			style={{ paddingTop: "3.5rem" }}
		>
			<p
				className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[2.75rem] relative shrink-0 text-[2rem] text-white tracking-[0.03125rem] w-full"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Our Values
			</p>
			<div
				className="relative flex flex-col flex-wrap justify-between w-full"
				style={{ gap: "2.5rem" }}
			>
				<FeatureDescriptionContainer6 />
				<FeatureDescriptionContainer7 />
				<FeatureDescriptionContainer8 />
			</div>
		</div>
	);
}

function PromoTextContainer() {
	return (
		<div
			className="content-stretch flex gap-[0.25rem] items-center relative shrink-0"
			data-name="Promo Text Container"
		>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #1C1B1F)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
			<p className="font-['Inter:Extra_Bold',sans-serif] font-extrabold leading-[1.5rem] not-italic relative shrink-0 text-[#12301b] text-[1rem] text-nowrap tracking-[0.01rem] whitespace-pre">
				Try Free for 14 days
			</p>
		</div>
	);
}

function TryFreeButton() {
	return (
		<motion.button
			className="bg-[#a7ff5e] h-[4rem] relative rounded-[4.25rem] shrink-0 cursor-pointer border-none p-0"
			data-name="Search"
			type="button"
			whileHover={{ scale: 1.02 }}
			whileTap={{ scale: 0.98 }}
			transition={{ type: "spring", stiffness: 400, damping: 17 }}
			style={{ cursor: "pointer" }}
		>
			<div className="box-border content-stretch flex gap-[0.25rem] h-[4rem] items-center justify-end overflow-clip px-[2rem] py-[0.5rem] relative rounded-[inherit]">
				<PromoTextContainer />
			</div>
			<div
				aria-hidden="true"
				className="absolute border border-[#dde3e9] border-solid inset-0 pointer-events-none rounded-[4.25rem] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)]"
			/>
		</motion.button>
	);
}

function WhatWeOffer() {
	return (
		<div
			className="relative content-stretch flex flex-row flex-wrap gap-8 items-center justify-between w-full"
			style={{ paddingTop: "3.5rem" }}
		>
			<p
				className="relative flex-1 font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[2.75rem] shrink-0 text-[2rem] text-white tracking-[0.03125rem]"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				What We Offer
			</p>
			<TryFreeButton />
		</div>
	);
}

function OurStoryContent() {
	return (
		<div className="relative flex flex-col gap-8 w-full px-[7.5rem] py-[2.5rem]">
			<p
				className="relative font-['Bricolage_Grotesque:ExtraBold',sans-serif] font-extrabold leading-[3.5rem] text-[2.25rem] text-white tracking-[0.03125rem] w-full"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Our Story
			</p>
			<p className="relative font-['Inter:Regular',sans-serif] font-normal leading-[2rem] not-italic text-[1.25rem] text-[rgba(255,255,255,0.7)] tracking-[0.03125rem] w-full">
				Tax Lien Vest was founded to solve a common investor challenge:
				tax lien investing is lucrative but time-consuming and
				confusing. Our founders built a platform that delivers
				institutional-grade property intelligence, once available only
				to insiders in an easy-to-use, investor-focused system.
			</p>
			<WhatWeOffer />
			<div className="relative flex flex-row flex-wrap gap-6 justify-between w-full">
				<FeatureContainer2 />
				<FeatureContainer3 />
				<FeatureContainer4 />
			</div>
			<OurValues />
			<WhyInvestorsTrustUs />
		</div>
	);
}

function JoinCommunityTitle() {
	return (
		<div className="relative box-border content-stretch flex flex-col gap-[1.25rem] items-center px-[2.5rem] py-0 text-center tracking-[0.03125rem] w-full max-w-[72.5625rem]">
			<p
				className="font-['Bricolage_Grotesque:ExtraBold',sans-serif] font-extrabold leading-[3.5rem] relative shrink-0 text-[3rem] text-white w-full"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Join the Tax Lien Vest Community
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[2rem] not-italic relative shrink-0 text-[1.25rem] text-[rgba(255,255,255,0.7)] w-full">
				Whether you&apos;re looking to diversify your portfolio, secure
				steady returns, or outpace competitors at auction, Tax Lien Vest
				is your trusted partner in profitable tax lien investing.
			</p>
		</div>
	);
}

function SearchIcon() {
	return (
		<div className="absolute inset-[32.143%]" data-name="Icon">
			<div
				className="absolute inset-[-6.25%]"
				style={
					{
						"--stroke-0": "rgba(18, 48, 27, 1)",
					} as React.CSSProperties
				}
			>
				<svg
					className="block size-full"
					fill="none"
					preserveAspectRatio="none"
					viewBox="0 0 23 23"
				>
					<path
						d={svgPaths.p170ab80}
						id="Icon"
						stroke="var(--stroke-0, #12301B)"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2.5"
					/>
				</svg>
			</div>
		</div>
	);
}

function SearchIconContainer() {
	return (
		<div
			className="bg-[#a7ff5e] h-full relative rounded-[2.75rem] shrink-0 w-[3.5rem]"
			data-name="Search Icon Container"
		>
			<div className="h-full overflow-clip relative rounded-[inherit] w-[3.5rem]">
				<SearchIcon />
			</div>
			<div
				aria-hidden="true"
				className="absolute border border-[#12301b] border-solid inset-0 pointer-events-none rounded-[2.75rem]"
			/>
		</div>
	);
}

function SearchInput() {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);

	const handleAddressSelect = (address: AddressData) => {
		if (address.latitude && address.longitude) {
			setIsLoading(true);
			const params = new URLSearchParams();
			params.set("lat", address.latitude.toString());
			params.set("lng", address.longitude.toString());
			params.set("address", encodeURIComponent(address.formattedAddress));
			router.push(`/playground?${params.toString()}`);
		}
	};

	return (
		<motion.div
			className="relative w-full"
			data-name="Search"
			style={{ zIndex: 1000, overflow: "visible" }}
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
			whileHover={{ scale: 1.02 }}
			whileTap={{ scale: 0.98 }}
		>
			<div className="overflow-visible">
				<SearchBar
					onAddressSelect={handleAddressSelect}
					isLoading={isLoading}
					fullWidth={true}
					inputClassName="!h-[4.5rem] !text-xl !px-12 !pr-16"
				/>
			</div>
		</motion.div>
	);
}

function PromoTextContainer1({ onClick }: { onClick?: () => void }) {
	return (
		<button
			onClick={onClick}
			className="relative content-stretch flex gap-[0.25rem] items-center cursor-pointer bg-transparent border-none p-0"
			data-name="Promo Text Container"
			type="button"
			style={{ cursor: "pointer" }}
		>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
			<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[0.875rem] not-italic relative shrink-0 text-[#cbf7a6] text-[0.75rem] text-nowrap tracking-[0.0075rem] whitespace-pre">
				<span>{`Try Free for 14 days • `}</span>
				<span className="text-[#dde3e9]">{`No Credit Card Required • Cancel Anytime  • Priority  Support`}</span>
			</p>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
		</button>
	);
}

function JoinCommunitySection({ onUpgrade }: { onUpgrade?: () => void }) {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter rounded-[1.8125rem] w-full flex flex-col items-center gap-8 py-[5rem]"
			style={{ overflow: "visible" }}
			data-name="Promo Section"
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<JoinCommunityTitle />
			<div className="relative w-[72%] max-w-[62rem] min-w-[18.75rem]">
				<SearchInput />
				<SampleSearchResultsPreview
					className="mt-5 w-full"
					title="Properties in New Castle"
					description="Real properties currently available in New Castle, Delaware."
				/>
			</div>
			<PromoTextContainer1 onClick={onUpgrade} />
		</motion.div>
	);
}

function MissionVisionRow() {
	return (
		<div className="relative flex flex-row flex-wrap gap-6 justify-between w-full px-[7.25rem]">
			<FeatureSection />
			<FeatureSection1 />
		</div>
	);
}

export default function AboutUsContent() {
	const [isUpgrading, setIsUpgrading] = useState(false);

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	return (
		<div
			className="bg-[#16251c] w-full flex flex-col gap-16"
			data-name="About US"
		>
			<ScalableContainer baseWidth={1440}>
				<div className="relative w-full flex flex-col gap-16">
					<AboutUsHeroSection />
					<MissionVisionRow />
					<OurStoryContent />
					<JoinCommunitySection onUpgrade={handleUpgrade} />
					<div className="relative w-full">
						<Footer showSearch={false} />
					</div>
				</div>
			</ScalableContainer>
		</div>
	);
}
